export const PARAGRAPH_MAX_GAP_SECONDS = 2.5;
export const PARAGRAPH_MAX_DURATION_SECONDS = 45;
export const PARAGRAPH_MAX_CHARACTERS = 600;

export interface TranscriptParagraphCue {
  id: string;
  speaker_label: string | null;
  start_s: number;
  end_s: number;
  text: string;
}

export interface TranscriptParagraph<TCue extends TranscriptParagraphCue = TranscriptParagraphCue> {
  speaker_label: string | null;
  start_s: number;
  end_s: number;
  text: string;
  cues: TCue[];
}

/**
 * Join cue text the same way annotation quote reconstruction does: a single
 * readable space between cues, with subtitle line breaks normalised.
 */
export function joinTranscriptCueText(cues: readonly Pick<TranscriptParagraphCue, 'text'>[]): string {
  return cues.map((cue) => cue.text).join(' ').replace(/\s+/g, ' ').trim();
}

function createParagraph<TCue extends TranscriptParagraphCue>(cue: TCue): TranscriptParagraph<TCue> {
  return {
    speaker_label: cue.speaker_label,
    start_s: cue.start_s,
    end_s: cue.end_s,
    text: joinTranscriptCueText([cue]),
    cues: [cue],
  };
}

/**
 * Groups an already ordered cue list for display only. A 2.5 second pause
 * covers natural speech cadence in subtitle exports. The 45 second and 600
 * character caps keep long single-speaker answers readable without splitting
 * an individual source cue or changing its persisted identity.
 */
export function groupTranscriptParagraphs<TCue extends TranscriptParagraphCue>(
  cues: readonly TCue[],
): Array<TranscriptParagraph<TCue>> {
  const paragraphs: Array<TranscriptParagraph<TCue>> = [];

  for (const cue of cues) {
    const paragraph = paragraphs.at(-1);
    const previousCue = paragraph?.cues.at(-1);
    if (!paragraph || !previousCue) {
      paragraphs.push(createParagraph(cue));
      continue;
    }

    const gapSeconds = cue.start_s - previousCue.end_s;
    const nextDuration = cue.end_s - paragraph.start_s;
    const nextText = joinTranscriptCueText([...paragraph.cues, cue]);
    const shouldSplit = cue.speaker_label !== paragraph.speaker_label
      || gapSeconds > PARAGRAPH_MAX_GAP_SECONDS
      || nextDuration > PARAGRAPH_MAX_DURATION_SECONDS
      || nextText.length > PARAGRAPH_MAX_CHARACTERS;

    if (shouldSplit) {
      paragraphs.push(createParagraph(cue));
      continue;
    }

    paragraph.cues.push(cue);
    paragraph.end_s = cue.end_s;
    paragraph.text = nextText;
  }

  return paragraphs;
}
